import React from 'react';
import styled from 'styled-components';
import { Box, Button, Flexbox, Icon, TextField, Typography } from '@hiiretail/synergy-ui';
import { UpdateUserDto, UserDto, UserType } from '../../../../api-client';
import { CommonMessages } from '../../../../common';
import { useUpdateOwnProfileForm } from '../../hooks/useUpdateOwnProfileForm';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ErrorResponse } from '../../../../common/types/errorRespose';
import { ErrorBox } from '../../../../common/components/ErrorBox/ErrorBox';
import { useAuth } from '@hiiretail/core';
import { CopyToClipboard } from '../../../../common/components/CopyToClipboard/CopyToClipboard';

type GeneralInfoFormProps = {
  user: UserDto;
  isUpdating: boolean;
  error: ErrorResponse | null;
  updateProfile: (updateUserDto: UpdateUserDto) => void;
};

type GeneralInfoFormComponent = React.FC<GeneralInfoFormProps>;

export const GeneralInfoFormWrapper = styled(Flexbox)`
  box-shadow: 0 1px 4px rgb(0 0 0 / 8%), 0 0 2px rgb(0 0 0 / 3%);
  padding: ${({ theme }) => theme.spacing(4)}px;
`;

export const Photo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 80px;
`;

export const Divider = styled.hr`
  height: 1px;
  margin: 0;
  padding: 0;
  background-color: #edecec;
  border: none;
`;

export const GeneralInfoForm: GeneralInfoFormComponent = ({
  user,
  error,
  isUpdating,
  updateProfile,
}) => {
  const intl = useIntl();
  const { control, formState, handleSubmit } = useUpdateOwnProfileForm();
  const { userInfo } = useAuth();

  const onSubmit = ({ email, name }: UpdateUserDto) => {
    updateProfile({
      email,
      name,
    });
  };

  return (
    <GeneralInfoFormWrapper
      container
      style={{ width: '100%' }}
      direction="column"
      justifyContent="space-between"
    >
      <Flexbox container item direction="column" gutter={4}>
        <Flexbox container item alignItems="center" gutter={3}>
          <Flexbox item>
            {userInfo?.picture ? (
              <Photo data-testid="user-profile-photo" src={userInfo?.picture || ''} alt="" />
            ) : (
              <Icon.Persons data-testid="user-profile-photo" size="lg" />
            )}
          </Flexbox>
          <Flexbox item container>
            <Typography variant="header3">{user.name}</Typography>
          </Flexbox>
        </Flexbox>
        <Flexbox container item direction="column" gutter={2}>
          <Flexbox item container>
            <Flexbox item style={{ width: '30%' }}>
              <Typography variant="body1" weight="bold">
                User ID:
              </Typography>
            </Flexbox>
            <Flexbox item container justifyContent="flex-start" style={{ width: '70%' }}>
              <CopyToClipboard
                text={user.id}
                variant="body1"
                ellipsis
                textStyle={{
                  color: '#0000008a',
                }}
                iconStyle={{
                  width: 'fit-content',
                  overflow: 'visible',
                }}
              />
            </Flexbox>
          </Flexbox>
          <Flexbox item container>
            <Flexbox item style={{ width: '30%' }}>
              <Typography variant="body1" weight="bold">
                User Profile ID:
              </Typography>
            </Flexbox>
            <Flexbox item container justifyContent="flex-start" style={{ width: '70%' }}>
              <CopyToClipboard
                text={user.userProfileId}
                variant="body1"
                ellipsis
                textStyle={{
                  color: '#0000008a',
                }}
                iconStyle={{
                  width: 'fit-content',
                  overflow: 'visible',
                }}
              />
            </Flexbox>
          </Flexbox>
          <Flexbox item container>
            <Flexbox item style={{ width: '30%' }}>
              <Typography variant="body1" weight="bold">
                External ID:
              </Typography>
            </Flexbox>
            <Flexbox item container justifyContent="flex-start" style={{ width: '70%' }}>
              <CopyToClipboard
                text={user.userProfileExternalUserId || 'not set'}
                variant="body1"
                ellipsis
                textStyle={{
                  color: user.userProfileExternalUserId ? '#0000008a' : 'rgba(0, 0, 0, 0.30)',
                }}
                iconStyle={{
                  width: 'fit-content',
                  overflow: 'visible',
                }}
              />
            </Flexbox>
          </Flexbox>
          <Flexbox item container>
            <Flexbox item style={{ width: '30%' }}>
              <Typography variant="body1" weight="bold">
                Status:
              </Typography>
            </Flexbox>
            <Flexbox item container justifyContent="flex-start" style={{ width: '70%' }}>
              <Typography variant="body1" color={() => '#0000008a'}>
                {user.status}
              </Typography>
            </Flexbox>
          </Flexbox>
          <Flexbox item container>
            <Flexbox item style={{ width: '30%' }}>
              <Typography variant="body1" weight="bold">
                Scope:
              </Typography>
            </Flexbox>
            <Flexbox item container justifyContent="flex-start" style={{ width: '70%' }}>
              <Typography variant="body1" color={() => '#0000008a'}>
                {user.scope}
              </Typography>
            </Flexbox>
          </Flexbox>
          <Flexbox item container>
            <Flexbox item style={{ width: '30%' }}>
              <Typography variant="body1" weight="bold">
                Type:
              </Typography>
            </Flexbox>
            <Flexbox item container justifyContent="flex-start" style={{ width: '70%' }}>
              <Typography variant="body1" color={() => '#0000008a'}>
                {user.type}
              </Typography>
            </Flexbox>
          </Flexbox>
          {user.type === UserType.Standalone && (
            <Flexbox item container>
              <Flexbox item style={{ width: '30%' }}>
                <Typography variant="body1" weight="bold">
                  Operator ID:
                </Typography>
              </Flexbox>
              <Flexbox item container justifyContent="flex-start" style={{ width: '70%' }}>
                <Typography variant="body1" color={() => '#0000008a'}>
                  {user.operatorId || 'not set'}
                </Typography>
              </Flexbox>
            </Flexbox>
          )}
        </Flexbox>
      </Flexbox>
      <Box mt={10}>
        <Divider />
      </Box>
      <Flexbox item container style={{ overflowY: 'auto', width: '100%' }}>
        <form style={{ width: '100%' }}>
          {error && <ErrorBox error={error} />}
          <Flexbox container direction="column" gutter={4}>
            <Flexbox item>
              <Controller
                name="email"
                defaultValue={user.email}
                control={control}
                render={({ field: { ref, value, onChange } }) => (
                  <TextField
                    fullWidth
                    id="hii-current-email"
                    autoComplete="current-hii-email"
                    label={intl.formatMessage(CommonMessages.email)}
                    name="email"
                    disabled={isUpdating}
                    type="text"
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    error={formState.errors?.email?.message as unknown as string}
                  />
                )}
              />
            </Flexbox>
            <Flexbox item>
              <Controller
                name="name"
                defaultValue={user.name}
                control={control}
                render={({ field: { ref, value, onChange } }) => (
                  <TextField
                    fullWidth
                    id="hii-current-name"
                    autoComplete="current-hii-name"
                    label="Displayed name"
                    name="name"
                    disabled={isUpdating}
                    type="text"
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    error={formState.errors?.name?.message as unknown as string}
                  />
                )}
              />
            </Flexbox>
            <Flexbox item container justifyContent="flex-end">
              <Button
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={
                  (formState?.errors && !!Object.entries(formState?.errors).length) ||
                  Object.keys(formState?.dirtyFields).length < 1 ||
                  isUpdating
                }
              >
                {isUpdating ? 'Updating ...' : 'Update'}
              </Button>
            </Flexbox>
          </Flexbox>
        </form>
      </Flexbox>
    </GeneralInfoFormWrapper>
  );
};
